import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Bounce, toast } from 'react-toastify';
import { get, del } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { getApiErrorMessage, toastApiError } from '../../helpers/apiError';
import { getUserAvatarUrl, resolveMediaUrl } from '../../helpers/userAvatar';
import PageHeader from '../../components/DashboardComponents/PageHeader';
import { AvatarGroup } from '../../components/DashboardComponents/AvatarGroup';
import ListingTabs from '../../components/Listing/ListingTabs';
import SearchInput from '../../components/Listing/SearchInput';
import FilterDropdown from '../../components/Listing/FilterDropdown';
import Pagination from '../../components/Listing/Pagination';
import { ICONS } from '../../constants/icons';
import CustomButton from '../../components/OnboardingComponents/CustomButton';
import CustomIconButton from '../../components/OnboardingComponents/CustomIconButton';
import EmptyState from '../../components/Common/EmptyState';
import TruncatedText from '../../components/Common/TruncatedText';
import DeleteConfirmation from '../../components/Common/DeleteConfirmation';
import {
  fetchArchivedProjects,
  permanentlyDeleteArchivedProject,
  restoreArchivedProject,
} from '../Profile/profileArchivedProjects';

/** Figma tabs: All → In-Progress → Past → Future → Archive */
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'in-progress', label: 'In-Progress' },
  { key: 'past', label: 'Past' },
  { key: 'future', label: 'Future' },
  { key: 'archive', label: 'Archive' },
];

const TOAST_OPTIONS = {
  position: 'bottom-center',
  theme: 'colored',
  transition: Bounce,
};
const PAGE_SIZE = 10;
const MAX_VISIBLE_CATEGORIES = 2;
const SKELETON_ROWS = 8;

const getRandomColor = (seed) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 85%, 90%)`;
};

const getRandomTextColor = (seed) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 90%, 40%)`;
};

const getRandomBorderColor = (seed) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 85%, 55%)`;
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const getDaysRemaining = (endDateString) => {
  if (!endDateString) return '—';
  const end = new Date(endDateString);
  const now = new Date();
  const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  return Number.isNaN(days) ? '—' : `${Math.max(0, days)} Days`;
};

const deriveStatus = (startDateString, endDateString) => {
  const now = new Date();
  const start = startDateString ? new Date(startDateString) : null;
  if (!start || Number.isNaN(start.getTime())) return 'future';
  if (now < start) return 'future';
  const end = endDateString ? new Date(endDateString) : null;
  if (end && !Number.isNaN(end.getTime()) && now > end) return 'past';
  return 'in-progress';
};

const getProjectCode = (name = '') =>
  name
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const normalizeStatus = (status) => {
  if (!status) return null;
  if (status === 'future' || status === 'upcoming') return 'future';
  if (status === 'present') return 'in-progress';
  if (status === 'past') return 'past';
  if (['in-progress', 'future', 'past'].includes(status)) return status;
  return null;
};

const formatStatusLabel = (status) => {
  if (status === 'in-progress') return 'In-Progress';
  if (status === 'past') return 'Past';
  if (status === 'future') return 'Future';
  if (status === 'archived') return 'Archived';
  return '—';
};

const extractCategories = (project) => {
  if (project.category) return [project.category];
  if (Array.isArray(project.categories) && project.categories.length > 0) {
    return project.categories
      .map((cat) => (typeof cat === 'string' ? cat : cat?.name || '—'))
      .filter(Boolean);
  }
  return [];
};

const memberDisplayName = (member) => {
  const user = member?.user || {};
  return (
    user.full_name ||
    [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
    member.full_name ||
    member.name ||
    user.name ||
    user.email ||
    'Collaborator'
  );
};

const memberAvatar = (member) => {
  const user = member?.user || {};
  return (
    getUserAvatarUrl(user) ||
    resolveMediaUrl(member.avatar) ||
    resolveMediaUrl(member.avatar_url) ||
    null
  );
};

/** Prefer latest_members (listing) with members fallback for legacy payloads. */
const getProjectMembers = project => {
  if (Array.isArray(project?.latest_members) && project.latest_members.length) {
    return project.latest_members
  }
  if (Array.isArray(project?.members) && project.members.length) {
    return project.members
  }
  return []
}

const isProjectOwner = (project, currentUser) => {
  if (!currentUser || !currentUser.id) return false
  if (project.ownerId === currentUser.id || project.user_id === currentUser.id) {
    return true
  }
  const member = getProjectMembers(project).find(
    (m) => m.user_id === currentUser.id && m.role === 'owner'
  )
  return !!member
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Project Name' },
  { value: 'start_date', label: 'Start Date' },
  { value: 'end_date', label: 'End Date' },
  { value: 'created_at', label: 'Created At' },
  { value: 'updated_at', label: 'Updated At' },
]

export const mapApiProject = (project) => {
  const members = getProjectMembers(project)
  return {
    id: project.id,
    code: getProjectCode(project.name),
    title: project.name || '—',
    categories: extractCategories(project),
    status: normalizeStatus(project.status) || deriveStatus(project.start_date, project.end_date),
    collaborators: members.map((member) => ({
      name: memberDisplayName(member),
      src: memberAvatar(member),
    })),
    daysRemaining: getDaysRemaining(project.end_date),
    startDate: formatDate(project.start_date),
    endDate: formatDate(project.end_date),
    rawStartDate: project.start_date,
    rawEndDate: project.end_date,
    rawCreatedAt: project.created_at,
    rawUpdatedAt: project.updated_at,
    ownerId: project.user_id,
    members,
    latest_members: project.latest_members || members,
    user_id: project.user_id,
    isCurrentUserActiveMember: project.is_current_user_active_member === true,
  }
}

/**
 * ProjectsListing — Figma 40004168:985
 */
const ProjectsListing = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState("");
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);
  const [categories, setCategories] = useState([]);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archivingProject, setArchivingProject] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [listVersion, setListVersion] = useState(0);
  const isArchiveTab = activeTab === 'archive';

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    let cancelled = false;
    const fetchCategories = async () => {
      const response = await get(endpoints.getCategories);
      if (!cancelled && response?.data?.success) {
        setCategories(
          (response.data.result || []).map((c) => ({
            value: c.id,
            label: c.name,
          }))
        );
      }
    };
    fetchCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setIsLoading(true);
      setError(null);

      if (isArchiveTab) {
        const result = await fetchArchivedProjects({
          page: currentPage,
          pageSize: PAGE_SIZE,
          search: debouncedSearchQuery.trim(),
          categoryIds: selectedFilters ? [selectedFilters] : [],
          sortBy,
          sortDirection,
          signal: controller.signal,
        });
        if (controller.signal.aborted || result?.aborted) return;
        setIsLoading(false);
        if (!result.ok) {
          setError('Failed to load archived projects');
          setProjects([]);
          setTotalItems(0);
          return;
        }
        setProjects(result.list || []);
        setTotalItems(result.total || 0);
        const lastPage = Math.max(1, result.lastPage || 1);
        if (currentPage > lastPage) setCurrentPage(lastPage);
        return;
      }

      const statusMap = {
        future: 'future',
        'in-progress': 'present',
        past: 'past',
      };
      const params = {
        search: debouncedSearchQuery,
        page: currentPage,
        page_size: PAGE_SIZE,
        'with[]': ['categories', 'latest_members.user'],
        sort_by: sortBy,
        sort_direction: sortDirection,
        collaborator_count: true,
      };
      if (selectedFilters) {
        params['category_ids[]'] = selectedFilters;
      }
      if (activeTab !== 'all' && statusMap[activeTab]) {
        params.status = statusMap[activeTab];
      }
      const response = await get(endpoints.getProjects, {
        params,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setIsLoading(false);
      if (response?.data?.success) {
        setProjects((response.data.result?.data || []).map(mapApiProject));
        const newTotal = response.data.result?.total || 0;
        setTotalItems(newTotal);
        const totalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));
        const safePage = Math.min(currentPage, totalPages);
        if (safePage !== currentPage) setCurrentPage(safePage);
      } else {
        setError(getApiErrorMessage(response, 'Failed to load projects'));
        setProjects([]);
        setTotalItems(0);
      }
    };

    load();
    return () => {
      controller.abort();
    };
  }, [
    currentPage,
    debouncedSearchQuery,
    activeTab,
    selectedFilters,
    sortBy,
    sortDirection,
    isArchiveTab,
    listVersion,
  ]);

  const handleTooltipEnter = (e, content) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
    setTooltipContent(content);
  };

  const handleTooltipLeave = () => {
    setTooltipContent(null);
  };

  const scheduleListRefreshAfterRemoval = (removedId) => {
    const remainingOnPage = projects.filter((project) => project.id !== removedId).length;
    const newTotal = Math.max(0, totalItems - 1);
    const newTotalPages = Math.max(1, Math.ceil(newTotal / PAGE_SIZE));

    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages);
      return;
    }
    if (remainingOnPage === 0 && currentPage > 1) {
      setCurrentPage((page) => page - 1);
      return;
    }
    setListVersion((version) => version + 1);
  };

  const handleArchiveConfirm = async () => {
    if (!archivingProject) return;
    setIsArchiving(true);
    try {
      const response = await del(endpoints.deleteProject(archivingProject), {
        skipErrorToast: true,
      });
      if (!response?.data?.success) {
        toastApiError(response, 'Failed to archive project');
        return;
      }
      toast.success(
        response?.data?.message || 'Project archived successfully',
        TOAST_OPTIONS
      );
      setArchiveModalOpen(false);
      scheduleListRefreshAfterRemoval(archivingProject);
      window.dispatchEvent(
        new CustomEvent('project-archived', { detail: { projectId: archivingProject } }),
      );
      setArchivingProject(null);
    } catch (err) {
      toastApiError(err, 'Failed to archive project');
    } finally {
      setIsArchiving(false);
    }
  };

  const handleRestoreConfirm = async () => {
    if (!restoreTarget?.id || restoringId) return;
    setRestoringId(restoreTarget.id);
    try {
      const result = await restoreArchivedProject(restoreTarget.id);
      if (!result.ok) {
        toastApiError(result.response, 'Failed to restore project');
        return;
      }
      toast.success(
        result.response?.data?.message || 'Project restored',
        TOAST_OPTIONS
      );
      window.dispatchEvent(
        new CustomEvent('project-restored', { detail: { projectId: restoreTarget.id } }),
      );
      setRestoreTarget(null);
      scheduleListRefreshAfterRemoval(restoreTarget.id);
    } finally {
      setRestoringId(null);
    }
  };

  const handlePermanentDeleteConfirm = async () => {
    if (!deleteTarget?.id || isDeleting) return;
    setIsDeleting(true);
    try {
      const result = await permanentlyDeleteArchivedProject(deleteTarget.id);
      if (!result.ok) {
        toastApiError(result.response, 'Failed to permanently delete project');
        return;
      }
      toast.success(
        result.response?.data?.message || 'Project permanently deleted',
        TOAST_OPTIONS
      );
      setDeleteTarget(null);
      scheduleListRefreshAfterRemoval(deleteTarget.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const openProject = (row) => {
    const projectId = isArchiveTab ? row.projectId : row.id;
    if (!projectId) return;
    navigate(`/projects/${projectId}/overview`);
  };

  return (
    <div className="page-content">
      <div className="projects-listing">
        <PageHeader
          className="projects-listing__header"
          title="Projects"
          subtitle="View and manage your projects, timelines, and collaborators."
          actions={
            <div className="projects-listing__controls">
              <SearchInput
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by Project Title..."
                iconSrc={ICONS.search}
              />
              <FilterDropdown
                filters={[
                  {
                    key: 'category',
                    type: 'select',
                    label: 'Category',
                    multiple: false,
                    searchable: true,
                    value: selectedFilters,
                    defaultValue: "",
                    onChange: (next) => {
                      const value = Array.isArray(next)
                        ? String(next[0] || "")
                        : String(next || "");
                      setSelectedFilters(value);
                      setCurrentPage(1);
                    },
                    options: categories,
                  },
                  {
                    key: 'sortBy',
                    type: 'sortBy',
                    label: 'Sort By',
                    value: sortBy,
                    defaultValue: 'created_at',
                    onChange: setSortBy,
                    options: SORT_OPTIONS,
                  },
                  {
                    key: 'sortDirection',
                    type: 'sortDirection',
                    label: 'Order',
                    value: sortDirection,
                    defaultValue: 'desc',
                    onChange: setSortDirection,
                  },
                ]}
              />
              <CustomButton
                variant="primary"
                size="md"
                trailingIconSrc={ICONS.arrowRight}
                trailingIconAlt=""
                onClick={() => navigate('/projects/create')}
              >
                Create Project
              </CustomButton>
            </div>
          }
        />

        <div className="projects-listing__toolbar">
          <ListingTabs
            items={TABS}
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="projects-listing__table-wrapper">
          <div
            className="projects-listing__table"
            aria-busy={isLoading || undefined}
          >
            <div className="projects-listing__table-row projects-listing__table-row--head">
              <div>Project Title</div>
              <div>Category</div>
              <div>Status</div>
              <div>Collaborators</div>
              <div>Days Remaining</div>
              <div>Start Date</div>
              <div>End Date</div>
              <div className="projects-listing__table-actions-head">Actions</div>
            </div>

            {isLoading &&
              Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <div
                  key={`skeleton-${i}`}
                  className="projects-listing__table-row projects-listing__table-row--skeleton"
                  aria-hidden="true"
                >
                  {Array.from({ length: 8 }).map((__, cellIdx) => (
                    <div
                      key={cellIdx}
                      className="projects-listing__skeleton-cell"
                    >
                      <div className="projects-listing__skeleton-bar" />
                    </div>
                  ))}
                </div>
              ))}

            {!isLoading &&
              projects.map((row) => (
                <div key={row.id} className="projects-listing__table-row">
                  <div className="projects-listing__table-cell projects-listing__table-cell--title">
                    <span
                      className="projects-listing__table-code"
                      style={{
                        background: getRandomColor(row.title),
                        color: getRandomTextColor(row.title),
                      }}
                    >
                      {row.code}
                    </span>
                    {isArchiveTab ? (
                      <TruncatedText className="projects-listing__table-title">
                        {row.title}
                      </TruncatedText>
                    ) : (
                      <TruncatedText
                        as="button"
                        type="button"
                        className="projects-listing__table-title-link"
                        onClick={() => openProject(row)}
                      >
                        {row.title}
                      </TruncatedText>
                    )}
                  </div>
                  <div className="projects-listing__table-cell projects-listing__table-cell--categories">
                    {row.categories.length > 0 ? (
                      <div className="projects-listing__table-categories">
                        {row.categories
                          .slice(0, MAX_VISIBLE_CATEGORIES)
                          .map((cat, idx) => (
                            <span
                              key={`${row.id}-cat-${idx}`}
                              className="projects-listing__table-category projects-listing__table-category--chip"
                              title={cat}
                              style={{
                                background: 'transparent',
                                color: getRandomTextColor(cat),
                                borderColor: getRandomBorderColor(cat),
                              }}
                            >
                              {cat}
                            </span>
                          ))}
                        {row.categories.length > MAX_VISIBLE_CATEGORIES && (
                          <span
                            className="projects-listing__table-category projects-listing__table-category--more"
                            onMouseEnter={(e) =>
                              handleTooltipEnter(
                                e,
                                row.categories
                                  .slice(MAX_VISIBLE_CATEGORIES)
                                  .join(', ')
                              )
                            }
                            onMouseLeave={handleTooltipLeave}
                          >
                            +{row.categories.length - MAX_VISIBLE_CATEGORIES}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="projects-listing__table-category">—</span>
                    )}
                  </div>
                  <div className="projects-listing__table-cell">
                    <span
                      className={`projects-listing__status-badge projects-listing__status-badge--${
                        row.status || 'unknown'
                      }`}
                    >
                      {formatStatusLabel(row.status)}
                    </span>
                  </div>
                  <div className="projects-listing__table-cell projects-listing__table-cell--center">
                    <AvatarGroup people={row.collaborators} size="xs" max={4} />
                  </div>
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{row.daysRemaining}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{row.startDate}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell">
                    <TruncatedText>{row.endDate}</TruncatedText>
                  </div>
                  <div className="projects-listing__table-cell projects-listing__table-cell--actions">
                    {isArchiveTab ? (
                      <>
                        <CustomButton
                          variant="secondary"
                          size="sm"
                          type="button"
                          disabled={restoringId === row.id}
                          onClick={() => setRestoreTarget(row)}
                        >
                          Restore
                        </CustomButton>
                        <CustomIconButton
                          iconSrc={ICONS.trash}
                          label="Permanently delete"
                          tone="danger"
                          size="sm"
                          onClick={() => setDeleteTarget(row)}
                        />
                      </>
                    ) : (
                      <>
                        <CustomButton
                          variant="secondary"
                          size="sm"
                          trailingIconSrc={ICONS.arrowRightBlack}
                          trailingIconAlt=""
                          onClick={() => openProject(row)}
                        >
                          View Detail
                        </CustomButton>
                        <CustomIconButton
                          iconSrc={ICONS.edit}
                          label="Edit"
                          size="sm"
                          disabled={!isProjectOwner(row, currentUser)}
                          onClick={() => navigate(`/projects/${row.id}/edit`)}
                        />
                        <CustomIconButton
                          iconSrc={ICONS.archive}
                          label="Archive"
                          tone="danger"
                          size="sm"
                          disabled={!row.isCurrentUserActiveMember}
                          onClick={() => {
                            setArchivingProject(row.id);
                            setArchiveModalOpen(true);
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}

            {!isLoading && error && (
              <EmptyState
                title={
                  isArchiveTab
                    ? 'Unable to load archived projects'
                    : 'Unable to load projects'
                }
                description={error}
              />
            )}
            {!isLoading && !error && projects.length === 0 && (
              <EmptyState
                title={
                  isArchiveTab ? 'No Archived Projects' : 'No Projects Yet'
                }
                description={
                  isArchiveTab
                    ? 'Archived projects matching this search will show up here.'
                    : 'Create your first project to get started.'
                }
                actionText={isArchiveTab ? undefined : 'Add Project'}
                onActionClick={
                  isArchiveTab ? undefined : () => navigate('/projects/create')
                }
              />
            )}
          </div>
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          itemLabel="Projects"
          prevIconSrc={ICONS.chevronLeft}
          nextIconSrc={ICONS.chevronRight}
        />

        <DeleteConfirmation
          isOpen={archiveModalOpen}
          onCancel={() => {
            if (isArchiving) return;
            setArchiveModalOpen(false);
            setArchivingProject(null);
          }}
          onConfirm={handleArchiveConfirm}
          title="Archive Project"
          message="Archive this project? You can restore it later from the Archive tab."
          cancelText="Cancel"
          confirmText="Archive Project"
          confirmLoadingText="Archiving…"
          isDeleting={isArchiving}
        />

        <DeleteConfirmation
          isOpen={Boolean(restoreTarget)}
          onCancel={() => !restoringId && setRestoreTarget(null)}
          onConfirm={handleRestoreConfirm}
          title="Restore project"
          message={`Restore “${restoreTarget?.title || 'this project'}” from the archive? It will return to your active projects.`}
          cancelText="Cancel"
          confirmText="Restore"
          confirmLoadingText="Restoring…"
          isDeleting={Boolean(restoringId)}
        />

        <DeleteConfirmation
          isOpen={Boolean(deleteTarget)}
          onCancel={() => !isDeleting && setDeleteTarget(null)}
          onConfirm={handlePermanentDeleteConfirm}
          title="Permanently delete project"
          message={`Permanently delete “${deleteTarget?.title || 'this project'}”? This cannot be undone.`}
          cancelText="Cancel"
          confirmText="Delete Forever"
          confirmLoadingText="Deleting…"
          isDeleting={isDeleting}
        />

        {tooltipContent && (
          <div
            className="projects-listing__tooltip"
            style={{
              position: 'fixed',
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translate(-50%, -100%)',
              zIndex: 1000,
            }}
          >
            {tooltipContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectsListing;
